import { useEffect, useRef } from "react";
import maplibregl from "maplibre-gl";
import polyline from "@mapbox/polyline";
import { bbox } from "@turf/turf";

export default function AthleteMap({ activities, eventRoutes }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const mapLoaded = useRef(false);
  const pendingData = useRef({ activities, eventRoutes });

  useEffect(() => {
    pendingData.current = { activities, eventRoutes };
    if (!mapInstance.current || !mapLoaded.current) return;
    updateLayers(mapInstance.current, activities, eventRoutes);
  }, [activities, eventRoutes]);

  useEffect(() => {
    if (mapInstance.current) return;
    mapInstance.current = new maplibregl.Map({
      container: mapRef.current,
      style: "https://tiles.openfreemap.org/styles/liberty",
      center: [5.3, 52.3],
      zoom: 6
    });

    mapInstance.current.addControl(new maplibregl.NavigationControl(), "top-right");

    mapInstance.current.on("load", () => {
      mapLoaded.current = true;
      const { activities, eventRoutes } = pendingData.current;
      updateLayers(mapInstance.current, activities, eventRoutes);
    });

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
        mapLoaded.current = false;
      }
    };
  }, []);

  return <div ref={mapRef} className="athlete-map" />;
}

function updateLayers(map, activities, eventRoutes) {
  addEventRouteLayer(map, eventRoutes || []);
  addActivityLayer(map, activities || []);
  fitBounds(map, activities || [], eventRoutes || []);
}

function addEventRouteLayer(map, eventRoutes) {
  const features = eventRoutes
    .filter(r => r.coordinates?.length > 1)
    .map(r => ({
      type: "Feature",
      properties: { color: r.color, name: r.name, dayNumber: r.dayNumber },
      geometry: { type: "LineString", coordinates: r.coordinates }
    }));

  const geojson = { type: "FeatureCollection", features };
  if (map.getSource("ap-event-routes")) {
    map.getSource("ap-event-routes").setData(geojson);
  } else {
    map.addSource("ap-event-routes", { type: "geojson", data: geojson });
    map.addLayer({
      id: "ap-event-routes",
      type: "line",
      source: "ap-event-routes",
      paint: {
        "line-color": ["get", "color"],
        "line-width": 4,
        "line-opacity": 0.6
      }
    });
  }
}

function addActivityLayer(map, activities) {
  const features = activities
    .filter(a => a.summary_polyline)
    .map((a, i) => ({
      type: "Feature",
      id: i,
      properties: { name: a.name, distance: (a.distance / 1000).toFixed(1), isTraining: a.isTraining },
      geometry: {
        type: "LineString",
        coordinates: polyline.decode(a.summary_polyline).map(([lat, lon]) => [lon, lat])
      }
    }));

  const geojson = { type: "FeatureCollection", features };
  if (map.getSource("ap-activities")) {
    map.getSource("ap-activities").setData(geojson);
  } else {
    map.addSource("ap-activities", { type: "geojson", data: geojson });
    map.addLayer({
      id: "ap-activities",
      type: "line",
      source: "ap-activities",
      paint: {
        "line-color": "#ce1531",
        "line-width": 2.5,
        "line-opacity": 0.8
      }
    });
  }
}

function fitBounds(map, activities, eventRoutes) {
  const allFeatures = [
    ...activities.filter(a => a.summary_polyline).map(a => ({
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: polyline.decode(a.summary_polyline).map(([lat, lon]) => [lon, lat])
      }
    })),
    ...eventRoutes.filter(r => r.coordinates?.length > 1).map(r => ({
      type: "Feature",
      geometry: { type: "LineString", coordinates: r.coordinates }
    }))
  ];

  if (allFeatures.length === 0) return;

  const bounds = bbox({ type: "FeatureCollection", features: allFeatures });
  map.fitBounds([[bounds[0], bounds[1]], [bounds[2], bounds[3]]], { padding: 40, maxZoom: 13 });
}
